//dependencies
import React, { useMemo, useState } from "react";
import { useParams } from "react-router-dom";

import { Pagination, SortChangedEvent, Table } from "@performyard/py-components";

import Button from "../../../../../components/Button";
// components
import { SideNavigationLink } from "../../../../../components/SideNavigation";
import getReviewCycleTableCols from "./components/ReviewCycleTableCols";
import ReviewCyclesFilters from "./components/ReviewCyclesFilters";
import ReviewCycleReadableFiltersList from "./components/ReviewCyclesReadableFilters";

// hooks
import { useSearchService, useSearchServicePrefetch } from "../../../../../api/hooks";
import { useFeatureFlagQuery } from "../../../../../hooks/useFeatureFlag";

// types
import { SERVICE_REVIEW_CYCLE } from "../../../../../api/services/defs/search-service";
import { ReviewCycle } from "../../../../../api/services/searchServiceResultTypes/reviewCycles";

// utils
import { getFiltersForSearchService } from "./utils";

//template
import InfoPageTemplate from "../../../../../pageTemplates/InfoPageTemplate";
import StandardToolbarActionRow from "../../../../../pageTemplates/InfoPageTemplate/StandardToolbarActionRow";
import useInfoPageTemplateQueryState from "../../../../../pageTemplates/InfoPageTemplate/useInfoPageTemplateQueryState";
// utils
import { CompanyReviewsNavigationLinks, REVIEWS_TABLE_LIMIT } from "../../constants";

type ReviewCyclesProps = {
	navLinks: SideNavigationLink[];
};

const ReviewCycles = ({ navLinks }: ReviewCyclesProps) => {
	const { page } = useParams<{ page: string }>();

	const {
		pageNumber,
		sort,
		filters,
		setPageNumber,
		setSort,
		setFilters,
		searchValue,
		debouncedSearchValue,
		setDebouncedSearch,
	} = useInfoPageTemplateQueryState({
		defaultSortName: "start_date",
		defaultSortDirection: "desc",
		defaultPageNumber: 1,
	});
	const [isFiltersPanelOpen, setIsFiltersPanelOpen] = useState(false);

	const { data, isLoading } = useSearchService<ReviewCycle>({
		service: SERVICE_REVIEW_CYCLE,
		filters: getFiltersForSearchService(filters, debouncedSearchValue),
		skip: (pageNumber - 1) * REVIEWS_TABLE_LIMIT,
		limit: REVIEWS_TABLE_LIMIT,
		sort,
		defaultErrorMessage: "There was an error getting Review Cycles",
	});

	//prefetch page before and 3 after
	useSearchServicePrefetch<ReviewCycle>(
		{
			service: SERVICE_REVIEW_CYCLE,
			filters: getFiltersForSearchService(filters, debouncedSearchValue),
			skip: (pageNumber - 2) * REVIEWS_TABLE_LIMIT,
			limit: REVIEWS_TABLE_LIMIT,
			sort,
		},
		pageNumber <= 1 ? 0 : 1
	);

	useSearchServicePrefetch<ReviewCycle>(
		{
			service: SERVICE_REVIEW_CYCLE,
			filters: getFiltersForSearchService(filters, debouncedSearchValue),
			skip: pageNumber * REVIEWS_TABLE_LIMIT,
			limit: REVIEWS_TABLE_LIMIT,
			sort,
		},
		3
	);

	const { value: featureFlag } = useFeatureFlagQuery("autoflow", false);

	const reviewCycles = data?.results || [];
	const totalCycles = data?.total || 0;

	const additionalFiltersCount = Object.keys(filters).length;

	const columnDefs = useMemo(() => getReviewCycleTableCols(), []);

	const onSortChanged = (event: SortChangedEvent<ReviewCycle>) => {
		const columnState = event.api.getColumnState();
		const sortedColumn = columnState.find((col) => col.sort);
		if (sortedColumn?.colId) {
			setSort({
				name: sortedColumn.colId,
				direction: sortedColumn.sort as "asc" | "desc",
			});
		}
	};

	const toolbarActionRow = (
		<StandardToolbarActionRow
			searchInput={searchValue}
			onSearchInputChange={(value) => setDebouncedSearch(value)}
			onFilterButtonPress={() => {
				setIsFiltersPanelOpen(true);
			}}
			activeFiltersCount={additionalFiltersCount}
			searchPlaceholder="Search reviews cycles..."
		/>
	);

	const toolbarInformationRow = (
		<ReviewCycleReadableFiltersList filters={filters} setFilters={(filters) => setFilters({ ...filters })} />
	);

	const drawer = (
		<ReviewCyclesFilters
			isOpen={isFiltersPanelOpen}
			onClose={() => setIsFiltersPanelOpen(false)}
			filters={filters}
			onApplyFilters={(newFilters) => {
				setFilters({ ...newFilters });
			}}
			automationEnabled={featureFlag}
		/>
	);

	const actionButtons = [
		<Button color="green" key="create-review-cycle-button" href="/review-cycle/create/">
			New Review Cycle
		</Button>,
	];

	const mainContent = (
		<div>
			<Table<ReviewCycle>
				autoHeight
				rowData={reviewCycles}
				columnDefs={columnDefs}
				loading={isLoading}
				getRowId={(params) => params.data._id}
				onSortChanged={onSortChanged}
				emptyStateText="No Review Cycles Found"
				initialState={{
					sort: {
						sortModel: sort.name ? [{ colId: sort.name, sort: sort.direction as "asc" | "desc" }] : [],
					},
				}}
				animateRows={false}
			/>
			{totalCycles > 0 && (
				<div className="mx-auto mt-5 flex w-full max-w-[400px] justify-center">
					<Pagination
						currentPage={pageNumber}
						pageCount={Math.ceil(totalCycles / REVIEWS_TABLE_LIMIT)}
						onPageChange={(pageNumber: number) => setPageNumber(pageNumber)}
					/>
				</div>
			)}
		</div>
	);

	return (
		<InfoPageTemplate
			toolbarActionRow={toolbarActionRow}
			toolbarInformationRow={toolbarInformationRow}
			actionButtons={actionButtons}
			mainContent={mainContent}
			drawer={drawer}
			navigationLinks={navLinks || CompanyReviewsNavigationLinks}
			activeNavigationLinkKey={page}
		/>
	);
};

export default ReviewCycles;
